<template>
  <b-form-group :label="label" :label-for="id">
    <div :id="id" class="image-selector rounded p-2" @click="openFileInput">
      <!-- File Preview Grid -->
      <div class="d-flex flex-wrap justify-content-center gap-2 w-100">
        <!-- Existing Files -->
        <div
          v-for="(file, index) in files"
          :key="`file-${index}`"
          class="position-relative file-wrapper"
        >
          <b-button
            variant="danger"
            @click.stop="removeFile(index)"
            style="position: absolute; top: 0px; right: 0; z-index: 1000"
            size="sm"
            class="btn-icon"
          >
            <feather-icon icon="TrashIcon" />
          </b-button>

          <b-overlay
            :show="loadingStates[index] || false"
            rounded="sm"
            opacity="0.5"
            spinner-variant="primary"
            class="w-100 h-100 rounded overflow-hidden"
          >
            <!-- For image files, show the image preview -->
            <img
              v-if="isImageFile(file.url)"
              :src="file.url"
              alt="File preview"
              class="w-100 h-100"
              style="object-fit: cover"
            />
            <!-- For non-image files, show file icon -->
            <div
              v-else
              class="w-100 h-100 d-flex flex-column align-items-center justify-content-center"
            >
              <feather-icon :icon="getFileIcon(file.url)" size="lg" class="text-primary" />
            </div>
          </b-overlay>
          <!-- File name display -->
          <div class="file-name">{{ file.name || getFileName(file.url) }}</div>
        </div>

        <!-- Add New File Button -->
        <div class="add-image-button rounded d-flex align-items-center justify-content-center">
          <feather-icon icon="PlusIcon" class="text-muted" size="2x" />
        </div>
      </div>

      <b-form-file
        v-model="fileInput"
        :placeholder="placeholder"
        :state="state"
        ref="fileInput"
        class="w-100"
        @change="handleFileChange"
        style="display: none"
        multiple
      />
    </div>
  </b-form-group>
</template>

<script>
import { APIService } from '@/utils/api';
import { BButton, BFormFile, BFormGroup, BOverlay } from 'bootstrap-vue';

export default {
  name: 'MultiFileUpload',
  components: {
    BFormFile,
    BButton,
    BFormGroup,
    BOverlay,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      required: true,
    },
    state: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingStates: {},
      fileInput: null,
      files: [],
    };
  },
  mounted() {
    console.log('[MultiFileUpload] Mounted with value:', this.value);
    // Initialize with existing files from value prop
    this.initializeFiles();
  },
  watch: {
    value: {
      handler(newVal) {
        console.log('[MultiFileUpload] Value changed:', newVal);
        this.initializeFiles();
      },
      deep: true,
    },
  },
  methods: {
    initializeFiles() {
      // Handle different value formats
      if (!this.value) {
        this.files = [];
        return;
      }

      let urlArray = [];

      // If value is already an array, use it directly
      if (Array.isArray(this.value)) {
        urlArray = this.value;
      }
      // If value is a string with commas, split it
      else if (typeof this.value === 'string' && this.value.includes(',')) {
        urlArray = this.value.split(',').filter((url) => url.trim().length > 0);
      }
      // If value is a single string, make it an array with one element
      else if (typeof this.value === 'string' && this.value.trim().length > 0) {
        urlArray = [this.value];
      }

      // Map URLs to file objects
      this.files = urlArray.map((url) => ({
        url,
        name: this.getFileName(url),
      }));

      console.log('[MultiFileUpload] Initialized files:', this.files);
    },
    isImageFile(url) {
      if (!url) return false;
      return (
        /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url) ||
        url.startsWith('blob:') ||
        url.startsWith('data:image/')
      );
    },
    getFileName(url) {
      if (!url) return '';

      // For blob URLs or data URLs, return a generic name
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        return 'File preview';
      }

      // Extract filename from URL
      const urlParts = url.split('/');
      let fileName = urlParts[urlParts.length - 1];

      // Remove query parameters if present
      if (fileName.includes('?')) {
        fileName = fileName.split('?')[0];
      }

      // Decode URI components
      try {
        fileName = decodeURIComponent(fileName);
      } catch (e) {
        // If decoding fails, use original filename
      }

      // Truncate long filenames
      if (fileName.length > 15) {
        const extension = fileName.split('.').pop();
        const name = fileName.substring(0, fileName.length - extension.length - 1);
        fileName = `${name.substring(0, 10)}...${extension}`;
      }

      return fileName;
    },
    getFileIcon(url) {
      if (!url) return 'FileIcon';

      const extension = url.split('.').pop().toLowerCase();

      switch (extension) {
        case 'pdf':
          return 'FileTextIcon';
        case 'doc':
        case 'docx':
          return 'FileTextIcon';
        case 'xls':
        case 'xlsx':
          return 'FileTextIcon';
        case 'ppt':
        case 'pptx':
          return 'FileTextIcon';
        case 'zip':
        case 'rar':
        case 'tar':
        case 'gz':
          return 'FileIcon';
        case 'txt':
          return 'FileTextIcon';
        default:
          return 'FileIcon';
      }
    },
    removeFile(index) {
      // Create a copy of the files array
      const updatedFiles = [...this.files];
      // Remove the file at the specified index
      updatedFiles.splice(index, 1);
      // Update local array
      this.files = updatedFiles;
      // Emit the updated array of URLs
      this.$emit(
        'input',
        updatedFiles.map((file) => file.url)
      );
    },
    openFileInput() {
      if (this.$refs.fileInput) {
        const nativeInput = this.$refs.fileInput.$el.querySelector('input[type="file"]');
        if (nativeInput) {
          nativeInput.click();
        }
      }
    },
    handleFileChange(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Process each file
      Array.from(files).forEach((file) => {
        // Add a placeholder for the new file
        const newIndex = this.files.length;
        const tempUrl = URL.createObjectURL(file);

        // Store both the URL and original filename
        const fileObj = {
          url: tempUrl,
          name: file.name,
        };

        // Create a new array with the new file object
        const updatedFiles = [...this.files, fileObj];
        this.files = updatedFiles;

        // Set loading state for this specific file
        this.$set(this.loadingStates, newIndex, true);

        // Use either store dispatch or APIService, depending on what's available
        const uploadMethod = APIService.uploadFile(file);

        // Upload the file
        Promise.resolve(uploadMethod)
          .then((url) => {
            // Update the temporary URL with the actual URL
            const finalFiles = [...this.files];
            finalFiles[newIndex].url = url;
            this.files = finalFiles;

            console.log('[MultiFileUpload] Uploaded files:', finalFiles);

            // Emit the updated array of URLs
            this.$emit(
              'input',
              finalFiles.map((file) => file.url)
            );
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
            // Remove the failed upload
            this.removeFile(newIndex);
          })
          .finally(() => {
            // Clear loading state
            this.$set(this.loadingStates, newIndex, false);
          });
      });

      // Reset file input
      this.fileInput = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 0.5rem;
}

.file-wrapper {
  width: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;

  .file-name {
    width: 100%;
    text-align: center;
    font-size: 0.75rem;
    margin-top: 0.25rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 120px;
  }

  .w-100.h-100 {
    height: 120px !important;
  }
}

.image-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-color: var(--secondary);
  border-width: 2px !important;
  border-style: dashed !important;

  cursor: pointer;

  &:hover {
    border-color: var(--primary);
    .add-image-button {
      background-color: var(--primary);
      opacity: 0.75;
    }
  }

  .add-image-button {
    background-color: var(--secondary);
    opacity: 0.5;
    width: 120px;
    height: 120px;
    position: relative;
  }
}
</style>
